
import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from '../i18n';
import { athletesAPI } from "../api";
import { toast } from "../components/common/Notifier";
import { AthleteEditModal } from "../components/admin/AthleteEditModal";

export const AdminPage = () => {
  const { t } = useTranslation();

  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadAthletes();
  }, []);

  const loadAthletes = async () => {
    try {
      setLoading(true);
      const response = await athletesAPI.adminList();
      setAthletes(response.data || []);
    } catch (error) {
      toast.error(t('admin.loadError', 'Помилка завантаження списку атлетів'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAccess = async (athleteId) => {
    try {
      await athletesAPI.toggleAccess(athleteId);
      toast.success(t('admin.accessUpdated', 'Статус доступу оновлено'));
      loadAthletes();
    } catch (error) {
      toast.error(t('admin.accessUpdateError', 'Помилка оновлення доступу'));
    }
  };

  const handleSetAccess = async (athleteId, days) => {
    try {
      await athletesAPI.setAccess(athleteId, days);
      toast.success(t('admin.accessSetDays', `Доступ встановлено на ${days} дн.`, { days }));
      loadAthletes();
    } catch (error) {
      toast.error(t('admin.accessSetError', 'Помилка встановлення доступу'));
    }
  };

  const handleDelete = async (athleteId) => {
    if (!window.confirm(t('admin.deleteConfirm', 'Ви впевнені, що хочете видалити цього атлета?'))) return;
    try {
      await athletesAPI.delete(athleteId);
      toast.success(t('admin.deleteSuccess', 'Атлета видалено'));
      loadAthletes();
    } catch (error) {
      toast.error(t('admin.deleteError', 'Помилка видалення'));
    }
  };

  const filteredAthletes = athletes.filter(a =>
    a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.telegram_id?.toString().includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">{t('common.loading', 'Завантаження...')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full max-w-[1800px] mx-auto px-4 py-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <h1 className="text-3xl font-bold text-gray-800">
          👥 {t('admin.title', 'Адмін-панель')}
        </h1>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder={t('common.search', 'Пошук...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={() => {
              setSelectedAthlete(null);
              setShowForm(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors shadow-sm"
          >
            {t('admin.addAthlete', '+ Додати')}
          </button>
        </div>
      </div>

      {/* Статистика */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="text-sm text-gray-500">{t('admin.totalAthletes', 'Всього атлетів')}</div>
          <div className="text-2xl font-bold text-gray-800 mt-1">{athletes.length}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="text-sm text-gray-500">{t('admin.withAccess', 'З доступом')}</div>
          <div className="text-2xl font-bold text-green-600 mt-1">
            {athletes.filter(a => a.has_access).length}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="text-sm text-gray-500">{t('admin.withoutAccess', 'Без доступу')}</div>
          <div className="text-2xl font-bold text-red-600 mt-1">
            {athletes.filter(a => !a.has_access).length}
          </div>
        </div>
      </div>

      {/* Список атлетов */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.thAthlete', 'Атлет')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.thStrava', 'Strava')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.thGoal', 'Ціль')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.thWeeklyVolume', 'Об’єм/тижд')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.thTelegramId', 'Telegram ID')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.thRegistration', 'Реєстрація')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.thLastLogin', 'Останній вхід')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.thVdot', 'VDOT')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.thStatus', 'Статус')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.thAccessUntil', 'Доступ до')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.thActions', 'Дії')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 text-sm">
              {filteredAthletes.map((athlete) => (
                <tr key={athlete.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="font-medium text-gray-900">
                          {athlete.name}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {athlete.language === 'ru' ? '🇷🇺' :
                           athlete.language === 'uk' ? '🇺🇦' : '🇬🇧'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {athlete.strava_connected ? (
                      <span className="text-green-600 font-medium">🟢 {t('admin.stravaConnected', 'підключено')}</span>
                    ) : (
                      <span className="text-gray-400">⚪ {t('admin.stravaDisconnected', 'немає')}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    {athlete.has_upcoming_goal
                      ? `${(athlete.has_upcoming_goal.distance_m / 1000).toFixed(1)} ${t('units.km', 'км')} · ${dayjs(athlete.has_upcoming_goal.date).format('DD.MM.YYYY')}`
                      : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    {athlete.weekly_volume_km ? `${athlete.weekly_volume_km} ${t('units.km', 'км')}` : '—'}
                    {athlete.intervals_per_week && (
                      <span className="text-gray-400"> · {athlete.intervals_per_week}×{t('units.intervals', 'інтер.')}</span>
                    )}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-gray-500 font-mono text-xs">
                    {athlete.telegram_id || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500 text-xs">
                    {athlete.created_at ? dayjs(athlete.created_at).format('DD.MM.YYYY') : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500 text-xs">
                    {athlete.last_login ? dayjs(athlete.last_login).format('DD.MM.YYYY HH:mm') : '—'}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap font-bold text-purple-600">
                    {athlete.current_vdot || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                      athlete.has_access
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {athlete.has_access ? t('admin.statusActive', '✅ Активний') : t('admin.statusBlocked', '❌ Заблокований')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500 text-xs">
                    {athlete.access_until
                      ? dayjs(athlete.access_until).format('DD.MM.YYYY')
                      : '∞'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedAthlete(athlete);
                          setShowForm(true);
                        }}
                        className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition-colors"
                        title={t('admin.edit', 'Редагувати')}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleToggleAccess(athlete.id)}
                        className={`p-1 rounded hover:bg-gray-100 transition-colors ${
                          athlete.has_access ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'
                        }`}
                        title={athlete.has_access ? t('admin.disableAccess', 'Вимкнути доступ') : t('admin.enableAccess', 'Увімкнути доступ')}
                      >
                        {athlete.has_access ? '🔴' : '🟢'}
                      </button>
                      <button
                        onClick={() => {
                          const days = prompt(t('admin.setDaysPrompt', 'На скільки днів надати доступ?'), '30');
                          if (days) {
                            handleSetAccess(athlete.id, parseInt(days));
                          }
                        }}
                        className="text-purple-600 hover:text-purple-800 p-1 rounded hover:bg-purple-50 transition-colors"
                        title={t('admin.setDaysAccess', 'Встановити доступ на N днів')}
                      >
                        📅
                      </button>
                      <button
                        onClick={() => handleDelete(athlete.id)}
                        className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition-colors"
                        title={t('admin.delete', 'Видалити')}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredAthletes.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            {searchTerm ? t('admin.notFound', 'Нічого не знайдено') : t('admin.noAthletes', 'Немає атлетів')}
          </div>
        )}
      </div>

      {showForm && (
        <AthleteEditModal
          athlete={selectedAthlete}
          onClose={() => setShowForm(false)}
          onSaved={loadAthletes}
        />
      )}
    </div>
  );
};